import { SOCKET_EVENT } from "@sendsar/protocol";
import type {
  CallAcceptedEvent,
  CallDeclinedEvent,
  CallEndedEvent,
  CallInviteEvent,
  OnlineUserIdsSnapshot,
  PresenceEvent,
  RoomIdPayload,
  RoomReadEvent,
  TypingEvent,
} from "@sendsar/protocol";
import { io, type Socket } from "socket.io-client";
import { SendsarError } from "./errors.js";
import { EventEmitter } from "./events.js";
import { RestClient } from "./rest.js";
import { socketOriginFromApiUrl } from "./url.js";

const DEFAULT_CONNECT_TIMEOUT_MS = 30_000;
import type {
  ConnectOptions,
  SendsarEvent,
  SendsarEventMap,
  SendsarInitOptions,
  JoinRoomParams,
  ListMessagesParams,
  ListRoomsParams,
  Message,
  SendMessageParams,
  RegisterDeviceTokenParams,
  DeviceTokenRecord,
  ToggleReactionParams,
  TypingParams,
  UpdateMessageParams,
  StartCallParams,
  StartCallResult,
  CallRecord,
} from "./types.js";

export class SendsarClient extends EventEmitter {
  readonly apiUrl: string;
  private readonly rest: RestClient;
  private socket: Socket | null = null;
  private connectedUserId: string | null = null;
  /** Rooms to re-join after `connect()` (e.g. token refresh). Cleared on `disconnect()`. */
  private readonly joinedRooms = new Map<string, string>();

  constructor(options: SendsarInitOptions) {
    super();
    this.apiUrl = options.apiUrl;
    this.rest = new RestClient(options.apiUrl);
  }

  get currentUserId(): string | null {
    return this.connectedUserId;
  }

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Replace the session JWT (e.g. after your backend refreshes it).
   * Does not reconnect the WebSocket — call `connect()` again if the socket dropped.
   */
  updateSessionToken(token: string): void {
    this.rest.setSessionToken(token);
  }

  /**
   * Connect with a session JWT from your backend (`POST /v1/auth/token` via your API).
   * Call `joinRoom` after connect when opening a conversation.
   * Re-call `connect()` after refreshing the token so the WebSocket uses the new JWT.
   * Previously joined rooms are re-joined automatically after reconnect.
   */
  async connect(options: ConnectOptions): Promise<void> {
    if (this.socket) {
      await this.teardownSocket();
    }

    this.emit("connecting", { userId: options.userId });

    this.connectedUserId = options.userId;
    this.rest.setSessionToken(options.token);

    const origin = socketOriginFromApiUrl(this.apiUrl);
    const socket = io(origin, {
      auth: { token: options.token },
      transports: ["websocket", "polling"],
      autoConnect: true,
    });

    this.socket = socket;
    this.bindSocket(socket);

    const timeoutMs = options.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS;

    await new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        cleanup();
        socket.disconnect();
        this.socket = null;
        this.connectedUserId = null;
        this.rest.setSessionToken(null);
        const err = new SendsarError(
          `Sendsar WebSocket connect timed out after ${timeoutMs}ms`,
          0,
          "",
        );
        this.emit(SOCKET_EVENT.ERROR, { message: err.message });
        reject(err);
      }, timeoutMs);

      const onConnect = () => {
        cleanup();
        this.emit("connected", { userId: options.userId });
        resolve();
      };
      const onError = (payload: { message?: string }) => {
        cleanup();
        const message = payload?.message ?? "WebSocket connection failed";
        this.emit(SOCKET_EVENT.ERROR, { message });
        reject(new Error(message));
      };
      const cleanup = () => {
        clearTimeout(timeoutId);
        socket.off("connect", onConnect);
        socket.off(SOCKET_EVENT.ERROR, onError);
        socket.off("connect_error", onConnectError);
      };
      const onConnectError = (err: Error) => {
        cleanup();
        this.emit(SOCKET_EVENT.ERROR, { message: err.message });
        reject(err);
      };
      socket.on("connect", onConnect);
      socket.on(SOCKET_EVENT.ERROR, onError);
      socket.on("connect_error", onConnectError);
    });

    this.rejoinTrackedRooms();
  }

  async disconnect(): Promise<void> {
    await this.teardownSocket();
    this.connectedUserId = null;
    this.rest.setSessionToken(null);
    this.joinedRooms.clear();
    this.emit("disconnected", undefined!);
  }

  on<E extends SendsarEvent>(
    event: E,
    handler: (payload: SendsarEventMap[E]) => void,
  ): () => void {
    return super.on(event, handler);
  }

  /** List rooms for the connected user. Supports filters via `ListRoomsParams`. */
  async listRooms(params?: ListRoomsParams) {
    return this.rest.listRooms(params);
  }

  /**
   * Load messages for a room (newest first from API). Use `nextCursor` from the
   * response to paginate older messages.
   */
  async getMessages(roomId: string, params?: ListMessagesParams) {
    return this.rest.listMessages(roomId, params);
  }

  /** Send a message. Pass `clientMessageId` for idempotent / optimistic UI dedupe. */
  async sendMessage(roomId: string, params: SendMessageParams): Promise<Message> {
    const senderId = params.senderId ?? this.requireUserId();
    return this.rest.sendMessage(roomId, { ...params, senderId });
  }

  /**
   * Replace message parts (sender only). Emits `message-updated` on connected clients.
   * Returns the updated message — merge into local state by `id`.
   */
  async updateMessage(
    roomId: string,
    messageId: string,
    params: UpdateMessageParams,
  ): Promise<Message> {
    const userId = params.userId ?? this.requireUserId();
    return this.rest.updateMessage(roomId, messageId, { ...params, userId });
  }

  /**
   * Soft-delete a message (sender only). Clears parts server-side.
   * Emits `message-updated` with `deletedAt` set.
   */
  async deleteMessage(roomId: string, messageId: string, userId?: string): Promise<Message> {
    const resolvedUserId = userId ?? this.requireUserId();
    return this.rest.deleteMessage(roomId, messageId, resolvedUserId);
  }

  /**
   * Toggle an emoji reaction for the connected user (add if missing, remove if present).
   */
  async toggleReaction(
    roomId: string,
    messageId: string,
    params: ToggleReactionParams,
  ): Promise<Message> {
    const userId = params.userId ?? this.requireUserId();
    return this.rest.toggleReaction(roomId, messageId, { ...params, userId });
  }

  async markRoomRead(roomId: string, lastReadMessageId?: string, userId?: string) {
    const resolvedUserId = userId ?? this.requireUserId();
    return this.rest.markRoomRead(roomId, resolvedUserId, lastReadMessageId);
  }

  async markRoomDelivered(
    roomId: string,
    lastDeliveredMessageId?: string,
    userId?: string,
  ): Promise<{ ok: true; lastDeliveredAt: string; lastDeliveredMessageId: string | null }> {
    const resolvedUserId = userId ?? this.requireUserId();
    return this.rest.markRoomDelivered(roomId, resolvedUserId, lastDeliveredMessageId);
  }

  /**
   * Register a push token for the connected user (offline notifications).
   * Call after `connect()` when the user opts in (OneSignal subscription id, FCM token, etc.).
   */
  async registerDeviceToken(params: RegisterDeviceTokenParams): Promise<DeviceTokenRecord> {
    this.requireUserId();
    return this.rest.registerDeviceToken(params);
  }

  async unregisterDeviceToken(token: string): Promise<void> {
    this.requireUserId();
    return this.rest.unregisterDeviceToken(token);
  }

  joinRoom(params: JoinRoomParams): void {
    this.assertSocket();
    const userId = params.userId ?? this.requireUserId();
    this.joinedRooms.set(params.roomId, userId);
    this.socket!.emit(SOCKET_EVENT.JOIN_ROOM, { roomId: params.roomId, userId });
  }

  leaveRoom(roomId: string, userId?: string): void {
    if (!this.socket?.connected) return;
    this.joinedRooms.delete(roomId);
    const resolvedUserId = userId ?? this.connectedUserId ?? undefined;
    this.socket.emit(
      SOCKET_EVENT.LEAVE_ROOM,
      resolvedUserId ? { roomId, userId: resolvedUserId } : roomId,
    );
  }

  setTyping(params: TypingParams): void {
    if (!this.socket?.connected) return;
    const userId = params.userId ?? this.requireUserId();
    this.socket.emit(SOCKET_EVENT.TYPING, {
      roomId: params.roomId,
      userId,
      isTyping: params.isTyping,
    });
  }

  async getActiveCall(roomId: string): Promise<CallRecord | null> {
    try {
      const { call } = await this.rest.getActiveCall(roomId);
      return call;
    } catch {
      return null;
    }
  }

  async startCall(roomId: string, params: StartCallParams): Promise<StartCallResult> {
    return this.rest.startCall(roomId, params);
  }

  async acceptCall(roomId: string, callId: string): Promise<StartCallResult> {
    return this.rest.acceptCall(roomId, callId);
  }

  async declineCall(roomId: string, callId: string): Promise<CallRecord> {
    return this.rest.declineCall(roomId, callId);
  }

  async endCall(
    roomId: string,
    callId: string,
    options?: { reason?: "cancelled" | "no_answer" },
  ): Promise<CallRecord> {
    return this.rest.endCall(roomId, callId, options);
  }

  async leaveCall(roomId: string, callId: string): Promise<CallRecord> {
    return this.rest.leaveCall(roomId, callId);
  }

  async refreshCallToken(roomId: string, callId: string) {
    return this.rest.refreshCallToken(roomId, callId);
  }

  /** Subscribe to incoming call invites. Returns unsubscribe. Requires `connect()`. */
  callInvite(handler: (payload: CallInviteEvent) => void): () => void {
    return this.on(SOCKET_EVENT.CALL_INVITE, handler);
  }

  /** Subscribe when another participant accepts. Returns unsubscribe. */
  callAccepted(handler: (payload: CallAcceptedEvent) => void): () => void {
    return this.on(SOCKET_EVENT.CALL_ACCEPTED, handler);
  }

  /** Subscribe when another participant declines. Returns unsubscribe. */
  callDeclined(handler: (payload: CallDeclinedEvent) => void): () => void {
    return this.on(SOCKET_EVENT.CALL_DECLINED, handler);
  }

  /** Subscribe when the call ends or is cancelled. Returns unsubscribe. */
  callEnded(handler: (payload: CallEndedEvent) => void): () => void {
    return this.on(SOCKET_EVENT.CALL_ENDED, handler);
  }

  private requireUserId(): string {
    if (!this.connectedUserId) {
      throw new Error("Not connected — call connect() first");
    }
    return this.connectedUserId;
  }

  private assertSocket(): void {
    if (!this.socket?.connected) {
      throw new Error("Not connected — call connect() first");
    }
  }

  private teardownSocket(): Promise<void> {
    const socket = this.socket;
    if (!socket) return Promise.resolve();

    this.socket = null;
    return new Promise<void>((resolve) => {
      if (!socket.connected) {
        socket.removeAllListeners();
        resolve();
        return;
      }
      socket.once("disconnect", () => {
        socket.removeAllListeners();
        resolve();
      });
      socket.disconnect();
    });
  }

  /** Re-emit `join-room` for rooms tracked before a reconnect (token refresh). */
  private rejoinTrackedRooms(): void {
    if (!this.socket?.connected || this.joinedRooms.size === 0) return;
    for (const [roomId, userId] of this.joinedRooms) {
      this.socket.emit(SOCKET_EVENT.JOIN_ROOM, { roomId, userId });
    }
  }

  private bindSocket(socket: Socket): void {
    socket.on(SOCKET_EVENT.ERROR, (payload: { message?: string }) => {
      this.emit(SOCKET_EVENT.ERROR, { message: payload?.message ?? "Unknown error" });
    });
    socket.on(SOCKET_EVENT.NEW_MESSAGE, (message: Message) => {
      this.emit(SOCKET_EVENT.NEW_MESSAGE, message);
    });
    socket.on(SOCKET_EVENT.MESSAGE_UPDATED, (message: Message) => {
      this.emit(SOCKET_EVENT.MESSAGE_UPDATED, message);
    });
    socket.on(SOCKET_EVENT.PRESENCE, (payload: PresenceEvent) => {
      this.emit(SOCKET_EVENT.PRESENCE, payload);
    });
    socket.on(SOCKET_EVENT.TENANT_PRESENCE, (payload: PresenceEvent) => {
      this.emit(SOCKET_EVENT.TENANT_PRESENCE, payload);
    });
    socket.on(SOCKET_EVENT.TENANT_PRESENCE_SNAPSHOT, (payload: OnlineUserIdsSnapshot) => {
      this.emit(SOCKET_EVENT.TENANT_PRESENCE_SNAPSHOT, payload);
    });
    socket.on(SOCKET_EVENT.PRESENCE_SNAPSHOT, (payload: OnlineUserIdsSnapshot) => {
      this.emit(SOCKET_EVENT.PRESENCE_SNAPSHOT, payload);
    });
    socket.on(SOCKET_EVENT.TYPING, (payload: TypingEvent) => {
      this.emit(SOCKET_EVENT.TYPING, payload);
    });
    socket.on(SOCKET_EVENT.ROOM_READ, (payload: RoomReadEvent) => {
      this.emit(SOCKET_EVENT.ROOM_READ, payload);
    });
    socket.on(SOCKET_EVENT.JOINED_ROOM, (payload: RoomIdPayload) => {
      this.emit(SOCKET_EVENT.JOINED_ROOM, payload);
    });
    socket.on(SOCKET_EVENT.LEFT_ROOM, (payload: RoomIdPayload) => {
      this.emit(SOCKET_EVENT.LEFT_ROOM, payload);
    });
    socket.on(SOCKET_EVENT.CALL_INVITE, (payload) => {
      this.emit(SOCKET_EVENT.CALL_INVITE, payload);
    });
    socket.on(SOCKET_EVENT.CALL_ACCEPTED, (payload) => {
      this.emit(SOCKET_EVENT.CALL_ACCEPTED, payload);
    });
    socket.on(SOCKET_EVENT.CALL_DECLINED, (payload) => {
      this.emit(SOCKET_EVENT.CALL_DECLINED, payload);
    });
    socket.on(SOCKET_EVENT.CALL_ENDED, (payload) => {
      this.emit(SOCKET_EVENT.CALL_ENDED, payload);
    });
  }
}
